import { Directive, nextTick } from 'vue';

/**
 * Removes focusable descendants from sequential Tab order while keeping
 * pointer interaction. Used on layout chrome (aside, header, footer) so
 * form field tabindex sequences are not interrupted by nav chrome.
 */
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const OBSERVER_KEY = '__excludeTabOrderObserver';

function excludeFocusables(root: HTMLElement): void {
  root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR).forEach((el) => {
    if (el.tabIndex === -1) {
      return;
    }
    el.tabIndex = -1;
  });
}

const excludeTabOrder: Directive<HTMLElement> = {
  mounted(el) {
    const apply = () => excludeFocusables(el);
    apply();
    nextTick(apply);

    const observer = new MutationObserver(apply);
    observer.observe(el, { childList: true, subtree: true });
    (el as HTMLElement & Record<string, unknown>)[OBSERVER_KEY] = observer;
  },

  updated(el) {
    excludeFocusables(el);
  },

  unmounted(el) {
    const keyed = el as HTMLElement & Record<string, unknown>;
    const observer = keyed[OBSERVER_KEY] as MutationObserver | undefined;
    observer?.disconnect();
    delete keyed[OBSERVER_KEY];
  },
};

export default excludeTabOrder;
